/**
 * The /demo shell: the marker bar, then the real DashboardLayout (which renders
 * the real pages through its own <Outlet/>) wrapped in the demo session.
 *
 * DashboardLayout takes its own /demo branch for two things only — nav links
 * are rebased onto /demo, and its fixed chrome sits below this bar.
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import DemoProvider from './DemoProvider';
import { getDemoIdentity, getDemoSource, subscribeDemoIdentity } from './identity';

/**
 * Height of the bar, mirrored by DashboardLayout's `top-10` offsets.
 *
 * Reads the identity store directly (not through DemoProvider's context) so
 * it says whose demo this is even before `DemoProvider`'s own re-render —
 * both read the same module-level store, and `DemoProvider` is the parent
 * that resolves `?from=` first, so by the time this renders the identity for
 * a demo token is already the right one.
 */
const DemoBar: React.FC = () => {
  const [searchParams] = useSearchParams();
  // `?from=<invite token>` — set by the invite page's "see the dashboard" link,
  // so the way back is to that person's own invite rather than the front page.
  const identity = React.useSyncExternalStore(subscribeDemoIdentity, getDemoIdentity);
  // The query string is dropped by in-tour navigation; the store remembers it.
  const from = searchParams.get('from') ?? getDemoSource();

  return (
    <div className="fixed inset-x-0 top-0 z-[70] flex h-10 items-center justify-between gap-3 bg-ink px-4 text-white">
      <p className="truncate font-mono text-[11px] uppercase tracking-eyebrow text-zinc-300">
        {/* The full sentence needs room; on a phone the short form still says
            both things that matter. */}
        <span className="hidden sm:inline">Demo dashboard for {identity.businessName}</span>
        <span className="sm:hidden">Demo dashboard</span>
        <span className="mx-2 text-zinc-600">·</span>
        <span className="normal-case tracking-normal text-zinc-400">sample data</span>
      </p>
      {from ? (
        <Link
          to={`/invite/${from}`}
          className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-medium text-ink transition-transform hover:scale-[1.03]"
        >
          Claim your site
        </Link>
      ) : (
        <a
          href="https://foundlio.com"
          className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-medium text-ink transition-transform hover:scale-[1.03]"
        >
          Claim your site
        </a>
      )}
    </div>
  );
};

const DemoDashboard: React.FC = () => (
  <DemoProvider>
    <DemoBar />
    <DashboardLayout />
  </DemoProvider>
);

export default DemoDashboard;
